<template>
  <div class="rtb-creative-page">
    <!-- 搜索区域 -->
    <div class="search-card">
      <div class="search-card__header">
        <span class="search-card__title">
          <i class="el-icon-search"></i> 创意查询
        </span>
        <el-button type="text" @click="toggleSearch">
          {{ searchExpanded ? '收起' : '展开' }}
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </el-button>
      </div>
      <transition name="fade-collapse">
        <div v-show="searchExpanded" class="search-card__body">
          <el-form :model="queryParams" inline class="search-form">
            <el-form-item label="媒体">
              <search-channel-select v-model="queryParams.mediaId" />
            </el-form-item>
            <el-form-item label="应用包名">
              <el-input
                v-model="queryParams.bundle"
                placeholder="请输入应用包名"
                clearable
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item label="创意ID">
              <el-input
                v-model="queryParams.ideaId"
                placeholder="请输入创意ID"
                clearable
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item label="创意标识">
              <el-input
                v-model="queryParams.creativeId"
                placeholder="请输入MD5标识"
                clearable
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item label="审核状态">
              <el-select v-model="queryParams.channelAuditStatus" placeholder="全部" clearable>
                <el-option label="待审核" :value="0" />
                <el-option label="审核通过" :value="1" />
                <el-option label="审核中" :value="2" />
                <el-option label="驳回" :value="3" />
              </el-select>
            </el-form-item>
            <el-form-item label="创意类型">
              <el-select v-model="queryParams.ideaType" placeholder="全部" clearable>
                <el-option label="Banner" :value="1" />
                <el-option label="开屏" :value="2" />
                <el-option label="插屏" :value="3" />
                <el-option label="原生" :value="4" />
                <el-option label="激励视频" :value="5" />
              </el-select>
            </el-form-item>
            <el-form-item label="创意形式">
              <el-select v-model="queryParams.ideaStyle" placeholder="全部" clearable>
                <el-option label="图片" :value="1" />
                <el-option label="视频" :value="2" />
              </el-select>
            </el-form-item>
          </el-form>
          <div class="search-card__actions">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">查询</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
          </div>
        </div>
      </transition>
    </div>

    <!-- 数据表格 -->
    <div class="table-card">
      <div class="table-card__header">
        <div class="table-card__header-left">
          <span class="table-card__title">创意列表</span>
          <el-button
            type="primary"
            size="mini"
            :disabled="selectedRows.length === 0"
            :loading="batchAuditLoading"
            @click="handleBatchQueryAudit"
          >
            批量查询审核 ({{ selectedRows.length }})
          </el-button>
          <el-button
            type="warning"
            size="mini"
            :disabled="selectedRows.length === 0"
            :loading="batchSubmitLoading"
            @click="handleBatchSubmitAudit"
          >
            批量提审 ({{ selectedRows.length }})
          </el-button>
        </div>
        <div class="table-card__header-right">
          <el-popover
            placement="bottom-end"
            width="280"
            trigger="click"
            popper-class="column-picker-popper"
          >
            <template #reference>
              <el-button size="mini" icon="el-icon-setting">选择展示字段</el-button>
            </template>
            <div class="column-picker-header">
              <el-checkbox :indeterminate="colPickerIndeterminate" v-model="colPickerAll" @change="handleColPickerAll">全选</el-checkbox>
              <el-button type="text" size="mini" @click="handleColPickerReset">恢复默认</el-button>
            </div>
            <div class="column-picker-list">
              <el-checkbox-group v-model="visibleColKeys" class="col-checkbox-group">
                <el-checkbox v-for="col in columnOptions" :key="col.key" :label="col.key" class="col-checkbox-item">{{ col.label }}</el-checkbox>
              </el-checkbox-group>
            </div>
          </el-popover>
          <span class="table-card__count">共 {{ pagination.total }} 条</span>
        </div>
      </div>
      <el-table
        v-loading="loading"
        :data="tableData"
        highlight-current-row
        size="mini"
        class="creative-table"
        :row-style="{ height: '44px' }"
        :cell-style="{ padding: '2px 0' }"
        :header-cell-style="{ background: '#2d2d2d', color: '#aaa', fontWeight: '600', padding: '4px 0', fontSize: '12px' }"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="36" />
        <el-table-column prop="ideaId" label="ID" width="60" align="center" v-if="visibleColKeys.includes('ideaId')">
          <template v-slot="{ row }">
            <span class="id-text cell-text" @click="copyCell(row.ideaId)">{{ row.ideaId }}</span>
          </template>
        </el-table-column>
        <el-table-column label="素材" width="52" align="center">
          <template v-slot="{ row }">
            <el-popover
              v-if="parseImgUrls(row.imgUrl).length > 0"
              placement="left-start"
              :width="300"
              trigger="hover"
            >
              <template #reference>
                <el-image
                  :src="resolveUrl(parseImgUrls(row.imgUrl)[0])"
                  class="preview-thumb"
                  fit="cover"
                />
              </template>
              <div>
                <el-image
                  v-for="(url, i) in parseImgUrls(row.imgUrl)"
                  :key="i"
                  :src="resolveUrl(url)"
                  style="width: 260px; margin-bottom: 4px"
                />
              </div>
            </el-popover>
            <el-popover
              v-else-if="row.videoUrl"
              placement="left-start"
              :width="320"
              trigger="hover"
            >
              <template #reference>
                <div class="preview-thumb video-thumb">
                  <i class="el-icon-video-camera-solid"></i>
                </div>
              </template>
              <video :src="resolveUrl(row.videoUrl)" controls style="width: 280px" />
            </el-popover>
            <span v-else class="no-preview">-</span>
          </template>
        </el-table-column>
        <el-table-column label="媒体侧广告ID / 媒体" min-width="100" v-if="visibleColKeys.includes('creativeId') || visibleColKeys.includes('channelName')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <div class="cell-dual">
              <span v-if="visibleColKeys.includes('creativeId')" class="cell-dual__primary cell-dual__primary--id cell-text" :title="row.creativeId" @click="copyCell(row.creativeId)">{{ row.creativeId }}</span>
              <span v-if="visibleColKeys.includes('channelName')" class="cell-dual__secondary cell-text" @click="copyCell(row.channelName)">{{ row.channelName || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="72" align="center" v-if="visibleColKeys.includes('channelAuditStatus')">
          <template v-slot="{ row }">
            <span class="status-dot" :class="'status-dot--' + row.channelAuditStatus"></span>
            <span class="status-label">{{ auditStatusText(row.channelAuditStatus) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100" align="center" v-if="visibleColKeys.includes('ideaType') || visibleColKeys.includes('ideaStyle')">
          <template v-slot="{ row }">
            <span v-if="visibleColKeys.includes('ideaType')" class="type-chip">{{ ideaTypeText(row.ideaType) }}</span>
            <span v-if="visibleColKeys.includes('ideaStyle')" class="style-chip" :class="row.ideaStyle === 2 ? 'style-chip--video' : ''">
              <i :class="row.ideaStyle === 1 ? 'el-icon-picture-outline' : 'el-icon-video-camera'"></i>
              {{ row.ideaStyle === 1 ? '图' : '频' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="标题" min-width="50" v-if="visibleColKeys.includes('ideaTitle')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.ideaTitle)">{{ row.ideaTitle }}</span>
          </template>
        </el-table-column>
        <el-table-column label="描述" min-width="100" v-if="visibleColKeys.includes('ideaDesc')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.ideaDesc)">{{ row.ideaDesc || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="应用名称" min-width="100" v-if="visibleColKeys.includes('appName')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.appName)">{{ row.appName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="应用包名" min-width="120" v-if="visibleColKeys.includes('bundle')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.bundle)">{{ row.bundle }}</span>
          </template>
        </el-table-column>
        <el-table-column label="尺寸" width="100" align="center" v-if="visibleColKeys.includes('width')">
          <template v-slot="{ row }">
            <span v-if="row.width && row.height">{{ row.width }}×{{ row.height }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="落地页" min-width="120" v-if="visibleColKeys.includes('landingPage')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="link-compact cell-text" @click="copyCell(row.landingPage)">{{ row.landingPage || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="DP地址" min-width="120" v-if="visibleColKeys.includes('dpAddress')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="link-compact cell-text" @click="copyCell(row.dpAddress)">{{ row.dpAddress || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="视频地址" min-width="120" v-if="visibleColKeys.includes('videoUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.videoUrl)">{{ row.videoUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="视频时长" width="80" align="center" v-if="visibleColKeys.includes('duration')">
          <template v-slot="{ row }">{{ row.duration || '-' }}</template>
        </el-table-column>
        <el-table-column label="前贴图" min-width="100" v-if="visibleColKeys.includes('preImageUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.preImageUrl)">{{ row.preImageUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="后贴图" min-width="100" v-if="visibleColKeys.includes('postImageUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.postImageUrl)">{{ row.postImageUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="开发者" min-width="100" v-if="visibleColKeys.includes('developerName')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.developerName)">{{ row.developerName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="版本号" width="80" align="center" v-if="visibleColKeys.includes('version')">
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.version)">{{ row.version }}</span>
          </template>
        </el-table-column>
        <el-table-column label="应用大小" width="80" align="center" v-if="visibleColKeys.includes('size')">
          <template v-slot="{ row }">{{ row.size || '-' }}</template>
        </el-table-column>
        <el-table-column label="应用介绍" min-width="120" v-if="visibleColKeys.includes('introUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.introUrl)">{{ row.introUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="权限URL" min-width="120" v-if="visibleColKeys.includes('permissionUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.permissionUrl)">{{ row.permissionUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="隐私URL" min-width="120" v-if="visibleColKeys.includes('privacyUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.privacyUrl)">{{ row.privacyUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="应用图标" min-width="100" v-if="visibleColKeys.includes('iconUrl')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.iconUrl)">{{ row.iconUrl }}</span>
          </template>
        </el-table-column>
        <el-table-column label="图标高度" width="80" align="center" v-if="visibleColKeys.includes('iconHeight')">
          <template v-slot="{ row }">{{ row.iconHeight || '-' }}</template>
        </el-table-column>
        <el-table-column label="图标宽度" width="80" align="center" v-if="visibleColKeys.includes('iconWidth')">
          <template v-slot="{ row }">{{ row.iconWidth || '-' }}</template>
        </el-table-column>
        <el-table-column label="备注" min-width="100" v-if="visibleColKeys.includes('remark')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.remark)">{{ row.remark }}</span>
          </template>
        </el-table-column>
        <el-table-column label="扩展字段1" min-width="100" v-if="visibleColKeys.includes('ext1')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.ext1)">{{ row.ext1 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="扩展字段2" min-width="100" v-if="visibleColKeys.includes('ext2')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.ext2)">{{ row.ext2 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="扩展字段3" min-width="100" v-if="visibleColKeys.includes('ext3')" show-overflow-tooltip>
          <template v-slot="{ row }">
            <span class="cell-text" @click="copyCell(row.ext3)">{{ row.ext3 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="140" align="center" v-if="visibleColKeys.includes('createTime')">
          <template v-slot="{ row }">
            <span class="time-compact cell-text" @click="copyCell(row.createTime)">{{ formatTime(row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" width="140" align="center" v-if="visibleColKeys.includes('updateTime')">
          <template v-slot="{ row }">
            <span class="time-compact cell-text" @click="copyCell(row.updateTime)">{{ formatTime(row.updateTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" align="center" fixed="right">
          <template v-slot="{ row }">
            <el-button type="text" size="mini" @click="handleQueryAudit(row)" :loading="row._auditLoading" class="op-btn">查询状态</el-button>
            <el-divider direction="vertical" />
            <el-button type="text" size="mini" @click="handleEdit(row)" class="op-btn">编辑</el-button>
            <el-divider direction="vertical" />
            <el-button type="text" size="mini" @click="handleSubmitAudit(row)" :loading="row._submitLoading" class="op-btn" style="color: #e6a23c">提审</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          background
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </div>

    <!-- 详情抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      title="创意详情"
      size="480px"
      :before-close="closeDrawer"
    >
      <div v-if="currentRow" class="detail-content">
        <div class="detail-section">
          <h4>基本信息</h4>
          <div class="detail-item"><label>创意ID：</label><span>{{ currentRow.ideaId }}</span></div>
          <div class="detail-item"><label>创意标识：</label><span>{{ currentRow.creativeId }}</span></div>
          <div class="detail-item"><label>媒体：</label><span>{{ currentRow.channelName || currentRow.mediaId }}</span></div>
          <div class="detail-item"><label>创意类型：</label><span>{{ ideaTypeText(currentRow.ideaType) }}</span></div>
          <div class="detail-item"><label>创意形式：</label><span>{{ currentRow.ideaStyle === 1 ? '图片' : '视频' }}</span></div>
          <div class="detail-item"><label>审核状态：</label><span>{{ auditStatusText(currentRow.channelAuditStatus) }}</span></div>
          <div class="detail-item"><label>尺寸：</label><span>{{ currentRow.width }}×{{ currentRow.height }}</span></div>
        </div>
        <div class="detail-section">
          <h4>内容信息</h4>
          <div class="detail-item"><label>标题：</label><span>{{ currentRow.ideaTitle || '-' }}</span></div>
          <div class="detail-item"><label>描述：</label><span>{{ currentRow.ideaDesc || '-' }}</span></div>
          <div class="detail-item"><label>落地页：</label><span class="link-text">{{ currentRow.landingPage || '-' }}</span></div>
          <div class="detail-item"><label>DP地址：</label><span class="link-text">{{ currentRow.dpAddress || '-' }}</span></div>
        </div>
        <div v-if="currentRow.appName" class="detail-section">
          <h4>应用信息</h4>
          <div class="detail-item"><label>应用名称：</label><span>{{ currentRow.appName }}</span></div>
          <div class="detail-item"><label>包名：</label><span>{{ currentRow.bundle || '-' }}</span></div>
          <div class="detail-item"><label>版本：</label><span>{{ currentRow.version || '-' }}</span></div>
          <div class="detail-item"><label>开发者：</label><span>{{ currentRow.developerName || '-' }}</span></div>
        </div>
        <div class="detail-item"><label>创建时间：</label><span>{{ currentRow.createTime }}</span></div>
        <div class="detail-item"><label>更新时间：</label><span>{{ currentRow.updateTime || '-' }}</span></div>
      </div>
    </el-drawer>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      title="编辑创意"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" label-width="100px" size="small">
        <el-form-item label="创意ID">
          <el-input :value="editForm.ideaId" disabled />
        </el-form-item>
        <el-form-item label="创意标识">
          <el-input :value="editForm.creativeId" disabled />
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="editForm.ideaTitle" placeholder="请输入标题" clearable />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="editForm.ideaDesc" type="textarea" :rows="2" placeholder="请输入描述" />
        </el-form-item>
        <el-form-item label="落地页">
          <el-input v-model="editForm.landingPage" placeholder="请输入落地页URL" clearable />
        </el-form-item>
        <el-form-item label="DP地址">
          <el-input v-model="editForm.dpAddress" placeholder="请输入DeepLink地址" clearable />
        </el-form-item>
        <el-form-item label="视频地址">
          <el-input v-model="editForm.videoUrl" placeholder="请输入视频URL" clearable />
        </el-form-item>
        <el-form-item label="图片地址">
          <el-input v-model="editForm.imgUrl" type="textarea" :rows="2" placeholder="多张图片用逗号分隔" />
        </el-form-item>
        <el-form-item label="应用包名">
          <el-input v-model="editForm.bundle" placeholder="请输入应用包名" clearable />
        </el-form-item>
        <el-form-item label="宽度">
          <el-input-number v-model="editForm.width" :min="0" controls-position="right" />
        </el-form-item>
        <el-form-item label="高度">
          <el-input-number v-model="editForm.height" :min="0" controls-position="right" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSaveEdit" :loading="editSaving">保 存</el-button>
        <el-button type="warning" @click="handleUpdateToKuaishou" :loading="kuaishouUpdating">
          保存并更新到快手
        </el-button>
      </template>
    </el-dialog>

    <!-- 快手审核结果对话框 -->
    <el-dialog
      v-model="auditResultVisible"
      title="快手审核查询结果"
      width="560px"
    >
      <el-table :data="auditResults" size="small">
        <el-table-column prop="ideaId" label="创意ID" width="80" align="center" />
        <el-table-column prop="creativeId" label="快手创意ID" min-width="140" show-overflow-tooltip />
        <el-table-column label="快手状态" width="100" align="center">
          <template v-slot="{ row }">
            <el-tag :type="kuaishouStatusTag(row.statusCode)" size="small" effect="dark">
              {{ kuaishouStatusText(row.statusCode) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="本地状态" width="80" align="center">
          <template v-slot="{ row }">
            <span class="status-dot" :class="'status-dot--' + row.localAuditStatus"></span>
            <span class="status-label">{{ auditStatusText(row.localAuditStatus) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="statusMsg" label="状态描述" min-width="160" show-overflow-tooltip />
      </el-table>
      <template #footer>
        <el-button type="primary" @click="auditResultVisible = false">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getRtbCreativeList, updateRtbCreative, queryKuaishouAudit, updateKuaishouCreative, submitKuaishouCreative } from '@/api/manage/creative/creative'
import SearchChannelSelect from '@/components/SearchChannelSelect/index.vue'

export default {
  name: 'RtbCreativePage',
  components: { SearchChannelSelect },
  data () {
    return {
      searchExpanded: true,
      loading: false,
      queryParams: {
        ideaId: '',
        creativeId: '',
        channelAuditStatus: null,
        ideaType: null,
        ideaStyle: null,
        mediaId: null,
        bundle: ''
      },
      tableData: [],
      pagination: {
        pageNum: 1,
        pageSize: 20,
        total: 0
      },
      drawerVisible: false,
      currentRow: null,
      editDialogVisible: false,
      editSaving: false,
      kuaishouUpdating: false,
      editForm: {
        ideaId: null,
        creativeId: '',
        ideaTitle: '',
        ideaDesc: '',
        landingPage: '',
        dpAddress: '',
        videoUrl: '',
        imgUrl: '',
        bundle: '',
        width: null,
        height: null
      },
      auditResultVisible: false,
      auditResults: [],
      selectedRows: [],
      batchAuditLoading: false,
      batchSubmitLoading: false,
      columnOptions: [
        { key: 'ideaId', label: 'ID', default: true },
        { key: 'creativeId', label: '创意标识', default: true },
        { key: 'channelName', label: '媒体名称', default: true },
        { key: 'channelAuditStatus', label: '审核状态', default: true },
        { key: 'ideaType', label: '创意类型', default: true },
        { key: 'ideaStyle', label: '创意形式', default: true },
        { key: 'ideaTitle', label: '标题', default: true },
        { key: 'ideaDesc', label: '描述', default: true },
        { key: 'appName', label: '应用名称', default: true },
        { key: 'bundle', label: '应用包名', default: false },
        { key: 'width', label: '尺寸', default: false },
        { key: 'landingPage', label: '落地页', default: true },
        { key: 'dpAddress', label: 'DP地址', default: false },
        { key: 'videoUrl', label: '视频地址', default: false },
        { key: 'duration', label: '视频时长', default: false },
        { key: 'preImageUrl', label: '前贴图', default: false },
        { key: 'postImageUrl', label: '后贴图', default: false },
        { key: 'developerName', label: '开发者', default: false },
        { key: 'version', label: '版本号', default: false },
        { key: 'size', label: '应用大小', default: false },
        { key: 'introUrl', label: '应用介绍', default: false },
        { key: 'permissionUrl', label: '权限URL', default: false },
        { key: 'privacyUrl', label: '隐私URL', default: false },
        { key: 'iconUrl', label: '应用图标', default: false },
        { key: 'iconHeight', label: '图标高度', default: false },
        { key: 'iconWidth', label: '图标宽度', default: false },
        { key: 'remark', label: '备注', default: false },
        { key: 'ext1', label: '扩展字段1', default: false },
        { key: 'ext2', label: '扩展字段2', default: false },
        { key: 'ext3', label: '扩展字段3', default: false },
        { key: 'createTime', label: '创建时间', default: true },
        { key: 'updateTime', label: '更新时间', default: false }
      ],
      visibleColKeys: []
    }
  },
  computed: {
    colPickerAll: {
      get () {
        return this.visibleColKeys.length === this.columnOptions.length
      },
      set (val) {
        // handled by handleColPickerAll
      }
    },
    colPickerIndeterminate () {
      const len = this.visibleColKeys.length
      return len > 0 && len < this.columnOptions.length
    }
  },
  mounted () {
    this.initVisibleCols()
    this.fetchData()
  },
  methods: {
    initVisibleCols () {
      this.visibleColKeys = this.columnOptions.filter(c => c.default).map(c => c.key)
    },
    handleColPickerAll (val) {
      this.visibleColKeys = val ? this.columnOptions.map(c => c.key) : []
    },
    handleColPickerReset () {
      this.initVisibleCols()
    },
    copyCell (text) {
      if (!text && text !== 0) return
      const str = String(text)
      if (navigator.clipboard) {
        navigator.clipboard.writeText(str).then(() => {
          this.$message.success('已复制')
        }).catch(() => {
          this.$message.error('复制失败')
        })
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = str
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          this.$message.success('已复制')
        } catch (e) {
          this.$message.error('复制失败')
        }
        document.body.removeChild(textarea)
      }
    },
    async fetchData () {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
          flag: 0,
          ...this.buildQueryParams()
        }
        const res = await getRtbCreativeList(params)
        if (res && res.data) {
          this.tableData = res.data.records || []
          this.pagination.total = res.data.total || 0
        }
      } catch (e) {
        console.error('查询创意列表失败', e)
      } finally {
        this.loading = false
      }
    },
    buildQueryParams () {
      const params = {}
      if (this.queryParams.ideaId) params.ideaId = this.queryParams.ideaId
      if (this.queryParams.creativeId) params.creativeId = this.queryParams.creativeId
      if (this.queryParams.channelAuditStatus !== null && this.queryParams.channelAuditStatus !== '') {
        params.channelAuditStatus = this.queryParams.channelAuditStatus
      }
      if (this.queryParams.ideaType !== null && this.queryParams.ideaType !== '') {
        params.ideaType = this.queryParams.ideaType
      }
      if (this.queryParams.ideaStyle !== null && this.queryParams.ideaStyle !== '') {
        params.ideaStyle = this.queryParams.ideaStyle
      }
      if (this.queryParams.mediaId !== null && this.queryParams.mediaId !== '') {
        params.mediaId = this.queryParams.mediaId
      }
      if (this.queryParams.bundle) params.bundle = this.queryParams.bundle
      return params
    },
    handleSearch () {
      this.pagination.pageNum = 1
      this.fetchData()
    },
    handleReset () {
      this.queryParams = {
        ideaId: '',
        creativeId: '',
        channelAuditStatus: null,
        ideaType: null,
        ideaStyle: null,
        mediaId: null,
        bundle: ''
      }
      this.handleSearch()
    },
    handleSizeChange (size) {
      this.pagination.pageSize = size
      this.pagination.pageNum = 1
      this.fetchData()
    },
    handleCurrentChange (page) {
      this.pagination.pageNum = page
      this.fetchData()
    },
    toggleSearch () {
      this.searchExpanded = !this.searchExpanded
    },
    formatTime (time) {
      if (!time) return '-'
      return time.replace(/\s00:00:00$/, '').replace(/(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}).*/, '$1 $2')
    },
    auditStatusText (status) {
      const map = { 0: '待审核', 1: '通过', 2: '审核中', 3: '驳回' }
      return map[status] || '未知'
    },
    auditStatusTag (status) {
      const map = { 0: 'info', 1: 'success', 2: 'warning', 3: 'danger' }
      return map[status] || 'info'
    },
    ideaTypeText (type) {
      const map = { 1: 'Banner', 2: '开屏', 3: '插屏', 4: '原生', 5: '激励视频' }
      return map[type] || '-'
    },
    kuaishouStatusText (code) {
      const map = { 0: '未审核', 1: '审核通过', 2: '审核不通过', 3: '已屏蔽', 9: '未查到', '-1': '请求失败' }
      return map[code] || '未知'
    },
    kuaishouStatusTag (code) {
      const map = { 0: 'info', 1: 'success', 2: 'danger', 3: 'danger', 9: 'warning', '-1': 'danger' }
      return map[code] || 'info'
    },
    parseImgUrls (imgUrl) {
      if (!imgUrl) return []
      if (Array.isArray(imgUrl)) {
        return imgUrl.filter(url => typeof url === 'string' && url.trim())
      }
      try {
        const parsed = JSON.parse(imgUrl)
        if (Array.isArray(parsed)) {
          return parsed.filter(url => typeof url === 'string' && url.trim())
        }
      } catch (e) {}
      return imgUrl.split(',')
        .map(url => url.trim().replace(/^\[?["']?|["']?\]?$/g, ''))
        .filter(url => url)
    },
    resolveUrl (url) {
      if (!url) return ''
      return url.indexOf('http') === 0 ? url : this.$imgURL + url
    },
    closeDrawer () {
      this.drawerVisible = false
      this.currentRow = null
    },
    handleSelectionChange (rows) {
      this.selectedRows = rows
    },
    async handleBatchQueryAudit () {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要查询的创意')
        return
      }
      this.batchAuditLoading = true
      try {
        const ideaIds = this.selectedRows.map(r => r.ideaId)
        const res = await queryKuaishouAudit(ideaIds)
        if (res && res.code === 200 && res.data) {
          this.auditResults = res.data
          this.auditResultVisible = true
          this.fetchData()
        } else {
          this.$message.error(res?.msg || '批量查询快手审核状态失败')
        }
      } catch (e) {
        this.$message.error('批量查询快手审核状态异常')
      } finally {
        this.batchAuditLoading = false
      }
    },
    async handleBatchSubmitAudit () {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要提审的创意')
        return
      }
      this.batchSubmitLoading = true
      let success = 0
      let fail = 0
      try {
        for (const row of this.selectedRows) {
          try {
            const res = await submitKuaishouCreative(row.ideaId)
            if (res && res.code === 200 && res.data) {
              const result = res.data
              if (result.statusCode === 1 || result.statusCode === 6) {
                success++
              } else {
                fail++
              }
            } else {
              fail++
            }
          } catch (e) {
            fail++
          }
        }
        this.$message.success(`批量提审完成：成功 ${success} 条，失败 ${fail} 条`)
        this.fetchData()
      } catch (e) {
        this.$message.error('批量提审异常')
      } finally {
        this.batchSubmitLoading = false
      }
    },
    async handleQueryAudit (row) {
      row._auditLoading = true
      try {
        const res = await queryKuaishouAudit([row.ideaId])
        if (res && res.code === 200 && res.data) {
          this.auditResults = res.data
          this.auditResultVisible = true
          this.fetchData()
        } else {
          this.$message.error(res?.msg || '查询快手审核状态失败')
        }
      } catch (e) {
        this.$message.error('查询快手审核状态异常')
      } finally {
        row._auditLoading = false
      }
    },
    handleEdit (row) {
      this.editForm = {
        ideaId: row.ideaId,
        creativeId: row.creativeId,
        ideaTitle: row.ideaTitle || '',
        ideaDesc: row.ideaDesc || '',
        landingPage: row.landingPage || '',
        dpAddress: row.dpAddress || '',
        videoUrl: row.videoUrl || '',
        imgUrl: row.imgUrl || '',
        bundle: row.bundle || '',
        width: row.width,
        height: row.height
      }
      this.editDialogVisible = true
    },
    async handleSaveEdit () {
      this.editSaving = true
      try {
        const res = await updateRtbCreative(this.editForm)
        if (res && res.code === 200) {
          this.$message.success('保存成功')
          this.editDialogVisible = false
          this.fetchData()
        } else {
          this.$message.error(res?.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存异常')
      } finally {
        this.editSaving = false
      }
    },
    async handleUpdateToKuaishou () {
      this.kuaishouUpdating = true
      try {
        const saveRes = await updateRtbCreative(this.editForm)
        if (!saveRes || saveRes.code !== 200) {
          this.$message.error(saveRes?.msg || '保存失败')
          return
        }
        const res = await updateKuaishouCreative(this.editForm.ideaId)
        if (res && res.code === 200 && res.data) {
          const result = res.data
          if (result.statusCode === 9) {
            this.$message.success('更新已提交快手，状态：' + (result.statusMsg || '成功'))
          } else if (result.statusCode === -1) {
            this.$message.error('快手更新失败：' + (result.statusMsg || '未知错误'))
          } else {
            this.$message.success('快手更新结果：' + (result.statusMsg || '已提交'))
          }
          this.editDialogVisible = false
          this.fetchData()
        } else {
          this.$message.error(res?.msg || '更新快手创意失败')
        }
      } catch (e) {
        this.$message.error('更新快手创意异常')
      } finally {
        this.kuaishouUpdating = false
      }
    },
    async handleSubmitAudit (row) {
      row._submitLoading = true
      try {
        const res = await submitKuaishouCreative(row.ideaId)
        if (res && res.code === 200 && res.data) {
          const result = res.data
          if (result.statusCode === 1 || result.statusCode === 6) {
            this.$message.success('提审成功，当前状态：审核中')
          } else if (result.statusCode === -1) {
            this.$message.error('提审失败：' + (result.statusMsg || '未知错误'))
          } else {
            this.$message.warning('提审结果：' + (result.statusMsg || '未知'))
          }
          this.fetchData()
        } else {
          this.$message.error(res?.msg || '提审失败')
        }
      } catch (e) {
        this.$message.error('提审异常')
      } finally {
        row._submitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rtb-creative-page {
  padding: 12px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-card {
  background: $--common-card-color;
  border-radius: 6px;
  padding: 12px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #ccc;

    i {
      margin-right: 4px;
      color: $--base-color;
    }
  }

  &__body {
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    margin-top: 10px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 6px;
  }
}

.search-form {
  :deep(.el-form-item) {
    margin-bottom: 8px;
    margin-right: 12px;
  }

  :deep(.el-form-item__label) {
    color: #999;
    font-size: 12px;
    padding-right: 6px;
  }

  :deep(.el-input),
  :deep(.el-select) {
    width: 160px;
  }
}

.table-card {
  background: $--common-card-color;
  border-radius: 6px;
  padding: 12px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #ccc;
  }

  &__count {
    font-size: 12px;
    color: #777;
  }
}

.creative-table {
  flex: 1;

  :deep(.el-table__body-wrapper) {
    overflow-y: auto;
  }

  :deep(.el-table__row) {
    transition: background 0.15s;
  }
}

.id-text {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  color: #bbb;
  font-weight: 500;
}

.preview-thumb {
  width: 32px;
  height: 32px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  display: block;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 8px rgba($--base-color, 0.3);
  }
}

.video-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  color: #888;
  font-size: 14px;
}

.no-preview {
  color: #555;
  font-size: 12px;
}

.cell-dual {
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.35;

  &__primary {
    font-size: 12px;
    color: #ddd;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    &--id {
      font-family: 'Menlo', 'Consolas', monospace;
      font-size: 11px;
      color: #bbb;
      line-height: 1.15;
    }
  }

  &__secondary {
    font-size: 11px;
    color: #777;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
  position: relative;
  top: -1px;

  &--0 { background: #909399; }
  &--1 { background: #67c23a; box-shadow: 0 0 4px rgba(103, 194, 58, 0.4); }
  &--2 { background: #e6a23c; box-shadow: 0 0 4px rgba(230, 162, 60, 0.4); }
  &--3 { background: #f56c6c; box-shadow: 0 0 4px rgba(245, 108, 108, 0.4); }
}

.status-label {
  font-size: 12px;
  color: #bbb;
}

.type-chip {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  background: rgba($--base-color, 0.12);
  color: $--base-color;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
}

.style-chip {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.06);
  color: #999;
  font-size: 11px;
  margin-left: 3px;
  line-height: 1.5;

  &--video {
    background: rgba(230, 162, 60, 0.12);
    color: #e6a23c;
  }

  i {
    margin-right: 1px;
    font-size: 10px;
  }
}

.link-compact {
  font-size: 12px;
  color: rgba($--base-color, 0.75);
  cursor: pointer;

  &:hover {
    color: $--base-color;
  }
}

.cell-text {
  cursor: pointer;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-compact {
  font-size: 11px;
  color: #888;
  font-family: 'Menlo', 'Consolas', monospace;
}

.op-btn {
  font-size: 12px !important;
  padding: 2px 4px !important;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}

.detail-content {
  padding: 0 16px 16px;
}

.detail-section {
  margin-bottom: 20px;

  h4 {
    font-size: 14px;
    color: #e0e0e0;
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
}

.detail-item {
  display: flex;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.6;

  label {
    flex: 0 0 80px;
    color: #909399;
    text-align: right;
    padding-right: 12px;
  }

  span {
    flex: 1;
    color: #e0e0e0;
    word-break: break-all;
  }

  .link-text {
    color: $--base-color;
    cursor: pointer;
  }
}

.fade-collapse-enter-active,
.fade-collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
  overflow: hidden;
}

.fade-collapse-enter-from,
.fade-collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
}
</style>

<style lang="scss">
.column-picker-popper {
  background: #2d2d2d !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  padding: 12px 16px !important;

  .column-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 8px;
  }

  .column-picker-list {
    max-height: 300px;
    overflow-y: auto;
  }

  .col-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .col-checkbox-item {
    display: flex;
    margin-right: 0;
    padding: 2px 0;
    font-size: 12px;
    color: #ccc;

    .el-checkbox__label {
      color: #ccc;
      font-size: 12px;
    }
  }
}
</style>
